/**
 * The hive (docs/spec/04 §1): Stinger, Moth and Monarch, two wing frames each, drawn as left
 * halves facing up. Original designs in the spirit of the 1981 bee, butterfly and boss.
 */

import { BLUE, CYAN, GOLD, GREEN, LIME, MAGENTA, ORANGE, PURPLE, RED, SKY, WHITE, YELLOW } from '../palette';
import type { SpriteDef } from './types';
import { mirror, recolour } from './types';

const STINGER_UP = [
  '........',
  '....R...',
  '.....R..',
  '......YY',
  '.C...YYY',
  'CCC..YRY',
  'CCCC.YYY',
  '.CCCCBBB',
  '..CCCBBB',
  '...CCYYY',
  '....CBBB',
  '.....YYY',
  '.....BBB',
  '......YY',
  '......BB',
  '.......Y',
];

const STINGER_DOWN = [
  '........',
  '....R...',
  '.....R..',
  '......YY',
  '.....YYY',
  '.....YRY',
  '.....YYY',
  '...CCBBB',
  '.CCCCBBB',
  'CCCCCYYY',
  'CCCC.BBB',
  '.CC..YYY',
  '.....BBB',
  '......YY',
  '......BB',
  '.......Y',
];

export const STINGER: SpriteDef = {
  frames: [mirror(STINGER_UP), mirror(STINGER_DOWN)],
  colours: { Y: YELLOW, B: BLUE, C: CYAN, R: RED },
  rotates: true,
};

const MOTH_OPEN = [
  '........',
  '....W...',
  '.....W..',
  'RR....WW',
  'RRRR..WS',
  'RRRRR.WW',
  'RWWRRRWW',
  'RWWRRRSS',
  'RRRRRRWW',
  '.RRRRRSS',
  '..RRR.WW',
  '.RRRR.SS',
  'RRRR..WW',
  'RRR...W.',
  '.R......',
  '........',
];

const MOTH_SHUT = [
  '........',
  '....W...',
  '.....W..',
  '......WW',
  '..RR..WS',
  '.RRRR.WW',
  '.RWRRRWW',
  '.RWRRRSS',
  '.RRRRRWW',
  '..RRRRSS',
  '...RR.WW',
  '..RRR.SS',
  '.RRR..WW',
  '.RR...W.',
  '........',
  '........',
];

export const MOTH: SpriteDef = {
  frames: [mirror(MOTH_OPEN), mirror(MOTH_SHUT)],
  colours: { R: RED, W: WHITE, S: SKY },
  rotates: true,
};

const MONARCH_WIDE = [
  '..G.....',
  '..GG...O',
  '...GG..O',
  '....GGOO',
  '.O..GGGG',
  'OOO.GYGG',
  'OOOGGYYG',
  'OOOGGGGG',
  'OO.GGOOG',
  'OO.GGGGG',
  'OOGGGGOO',
  'OGGG.GGG',
  'OGG..GG.',
  'GG...GG.',
  'G....G..',
  '........',
];

const MONARCH_TUCKED = [
  '...G....',
  '...GG..O',
  '....G..O',
  '....GGOO',
  '....GGGG',
  '.OO.GYGG',
  '.OOGGYYG',
  '.OOGGGGG',
  '.OOGGOOG',
  '.OOGGGGG',
  '.OGGGGOO',
  '.OGG.GGG',
  '.GGG.GG.',
  '.GG..GG.',
  '..G..G..',
  '........',
];

export const MONARCH: SpriteDef = {
  frames: [mirror(MONARCH_WIDE), mirror(MONARCH_TUCKED)],
  colours: { G: GREEN, O: GOLD, Y: YELLOW },
  rotates: true,
};

/** After its first hit. */
export const MONARCH_HIT: SpriteDef = recolour(MONARCH, { G: PURPLE, O: SKY, Y: WHITE });

const SCARAB = [
  '........',
  '..O....O',
  '...O..OO',
  '....OOOO',
  '...OOYYO',
  '..OOOOOO',
  '.OOROOOY',
  'OOORROOY',
  'OOOOROOY',
  'OOOOOOOY',
  '.OOOOOOY',
  'O.OOOOOO',
  'O..OOOO.',
  '....OO..',
  '...O....',
  '........',
];

const MANTIS = [
  'L.......',
  'LL....L.',
  '.LL...LL',
  '..LL..WL',
  '...LLLLL',
  '....LLLL',
  '.L...LLG',
  'LLL.LLGG',
  'L.LLLLGG',
  '....LLGG',
  '...LLLGG',
  '..LL.LLG',
  '.LL..LLL',
  '.L....LL',
  '.......L',
  '........',
];

const HORNET = [
  '........',
  '.....M..',
  '......MM',
  '.....MWM',
  'P....MMM',
  'PP..MMMM',
  'PPP.MYYM',
  'PPPPMMMM',
  '.PPPMYYM',
  '..PPMMMM',
  '...PMYYM',
  '....MMMM',
  '.....MYY',
  '.....MMM',
  '......MM',
  '.......M',
];

/** The three morphs (02 §5), by type. */
export const MORPHS: readonly SpriteDef[] = [
  { frames: [mirror(SCARAB)], colours: { O: ORANGE, Y: YELLOW, R: RED }, rotates: true },
  { frames: [mirror(MANTIS)], colours: { L: LIME, G: GREEN, W: WHITE }, rotates: true },
  { frames: [mirror(HORNET)], colours: { M: MAGENTA, P: PURPLE, Y: YELLOW, W: WHITE }, rotates: true },
];
